'use client';

import type {
  ProductDetailMedia,
  ProductDetailVersion,
} from 'src/types/product-rankings/product-rankings-ops';

import { useMemo, useState, useEffect } from 'react';

import {
  Box,
  Alert,
  Stack,
  Button,
  Divider,
  Tooltip,
  Collapse,
  Typography,
  CircularProgress,
} from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { Tag } from './market-rankings-badges';
import AutoPlayVideo from './market-rankings-auto-play-video';
import { ymdToLong, ymdToShort } from '../utils/market-rankings-format';
import { CHANGE_TYPE_LABEL } from '../constants/market-rankings-labels';
import DetailVersionTimeline from './market-rankings-detail-version-timeline';
import { useProductDetailVersion } from '../hooks/load/fetch/use-product-detail-version';


/** 접힌 상태에서 보이는 높이 — 판매몰의 「상세정보 더보기」와 같은 방식 */
const FOLD_HEIGHT = 560;
const FOLD_HEIGHT_DENSE = 420;
/** 상세페이지 원본이 보통 이 폭으로 만들어진다 */
const PAGE_WIDTH = 560;
/** 접힌 동안 미리 그리는 컷 수 — 영상 수십 개를 미리 물지 않으려는 것 */
const FOLDED_RENDER_COUNT = 8;

/** 툴바의 「전체 펼치기」 신호. `seq`가 바뀔 때마다 카드들이 일괄 동기화된다 */
export type DetailExpandSignal = { seq: number; open: boolean };

/**
 * 상세페이지가 몇 번, **어떻게** 바뀌었나.
 *
 * ⚠️ `current`는 버전 인덱스가 있을 때만 찬다. 없으면(옛 수집분) 날짜·개수만 있고
 * 종류·증감은 `null`이다 — 배지는 뜨되 툴팁이 얇아진다.
 */
export type DetailVersionInfo = {
  mediaDate?: string;
  changedAt?: string;
  versionCount?: number;
  current?: {
    changeType?: string;
    addedCount?: number;
    removedCount?: number;
  } | null;
  /**
   * 개편 타임라인 전체(오름차순). **이것이 있어야 버전을 갈아 볼 수 있다** — 없으면
   * 지금까지처럼 배지 한 줄만 뜬다(비교 카드가 그 갈래다).
   */
  timeline?: ProductDetailVersion[];
  /** 지금 화면에 그려진 컷들의 버전 해시 — 타임라인에서 「현재」 칸을 찍는 근거 */
  shownHash?: string;
  /** 옛 버전을 되살릴 좌표. `kind`는 안 쓴다(버전 인덱스에 없다) */
  ref?: { postId: string; source: string };
};

/** 배지에 적을 말. `none`·`new`는 배지가 뜨는 상황이 아니라 일반 문구로 떨어뜨린다 */
export function versionChangeLabel(version?: DetailVersionInfo): string {
  const type = version?.current?.changeType;
  const label = type ? CHANGE_TYPE_LABEL[type] : '';
  if (!label || type === 'none' || type === 'new') return '개편';
  return label;
}

/** 툴팁에 적을 말 — 버전 수 · 시작일 · 직전 대비 증감 */
export function versionChangeHint(version?: DetailVersionInfo): string {
  const current = version?.current ?? null;
  const parts: string[] = [];
  if ((version?.versionCount ?? 0) > 1) {
    parts.push(`수집 구간에서 ${version?.versionCount}개 버전이 확인됐습니다.`);
  }
  parts.push(`현재 버전은 ${ymdToLong(version?.changedAt)} 부터입니다.`);

  const added = current?.addedCount ?? 0;
  const removed = current?.removedCount ?? 0;
  if (added || removed) {
    const diff: string[] = [];
    if (added) diff.push(`컷 ${added}개 추가`);
    if (removed) diff.push(`컷 ${removed}개 삭제`);
    parts.push(`직전 버전 대비 ${diff.join(' · ')}.`);
  } else if (current?.changeType === 'text-only') {
    parts.push('이미지 구성은 그대로고 문구(텍스트)만 바뀌었습니다.');
  }
  return parts.join(' ');
}

/**
 * 판매처 상세페이지를 원본 순서대로 세로로 이어 붙인다.
 *
 * ⚠️ **전체 펼치기 신호는 `seq`로 온다.** 값(`open`)만 보면 같은 값을 두 번 눌렀을 때
 * 반응하지 않는다. 신호로 일괄 동기화한 뒤에도 카드별로 따로 접었다 펴는 것이 그대로 된다.
 *
 * ⚠️ 초기값에도 신호를 반영한다 — 전체를 펼친 뒤 스크롤로 새로 그려지는 카드가 접힌 채
 * 나타나면 안 된다.
 */
export default function DetailMediaBlock({
  media,
  textAll,
  dense,
  version,
  expandSignal,
  noDivider,
}: {
  media: ProductDetailMedia[] | null | undefined;
  textAll?: string;
  dense?: boolean;
  version?: DetailVersionInfo;
  expandSignal?: DetailExpandSignal;
  /** ⚠️ 창의 맨 위에 놓일 때. 상세 본문에서는 앞 블록과 가르는 선이 필요하다 */
  noDivider?: boolean;
}) {
  const signalSeq = expandSignal?.seq || 0;
  const signalOpen = Boolean(expandSignal?.open);

  const [expanded, setExpanded] = useState(signalSeq > 0 && signalOpen);
  const [showText, setShowText] = useState(false);
  /** 사용자가 고른 옛 판. `null`이면 현재를 본다 */
  const [pickedSeq, setPickedSeq] = useState<number | null>(null);

  const timeline = version?.timeline;

  /**
   * 지금 그려진 컷들이 **몇 번째 판인가.**
   *
   * ⚠️ 마지막 칸이라고 단정하지 않는다 — 확장 수집이 상위 N개만이라 미디어가 옛 스냅샷에서
   * 올라오는 경우가 있다. 해시로 맞추고, 못 맞추면 그때만 마지막 칸으로 떨어진다.
   */
  const currentSeq = useMemo(() => {
    if (!timeline?.length) return null;
    const matched = version?.shownHash
      ? timeline.find((item) => item.hash === version.shownHash)
      : null;
    return (matched ?? timeline[timeline.length - 1])?.seq ?? null;
  }, [timeline, version?.shownHash]);

  /**
   * ⚠️ 고른 판이 없으면(현재를 본다) **아무것도 안 부른다.** 현재 상세는 이미
   * `rankings.detail`이 통째로 들고 있어서, 같은 것을 한 번 더 물으면 왕복만 는다.
   */
  const versionState = useProductDetailVersion(version?.ref, pickedSeq);

  useEffect(() => {
    if (!signalSeq) return;
    setExpanded(signalOpen);
  }, [signalSeq, signalOpen]);

  /** ⚠️ 상품을 갈아타면 고른 판을 놓는다 — 안 놓으면 새 상품에 남의 `seq`가 걸린다 */
  const refKey = `${version?.ref?.postId || ''}|${version?.ref?.source || ''}`;
  useEffect(() => {
    setPickedSeq(null);
  }, [refKey]);

  if (!media?.length) return null;

  const viewingPast = pickedSeq != null;
  const pastData = viewingPast ? versionState.data : null;

  /** ⚠️ 옛 판을 고르는 «순간»에도 이전 컷이 남아 있으면 안 된다 — 로딩 중에는 비운다 */
  const shownMedia: ProductDetailMedia[] = viewingPast ? (pastData?.media ?? []) : media;
  const shownTextAll = viewingPast ? pastData?.textAll || '' : textAll;

  const videoCount = shownMedia.filter((item) => item.type === 'video').length;
  const hints: string[] = [];
  if (!viewingPast && version?.mediaDate) hints.push(`${ymdToShort(version.mediaDate)} 수집`);
  if (videoCount) hints.push(`영상 ${videoCount}개`);
  const rendered = expanded ? shownMedia : shownMedia.slice(0, FOLDED_RENDER_COUNT);

  const backToCurrent = () => setPickedSeq(null);

  return (
    <>
      {noDivider ? null : <Divider sx={{ my: 2 }} />}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
        <Iconify icon="solar:gallery-linear" width={20} sx={{ color: 'secondary.main' }} />
        <Typography sx={{ fontWeight: 600 }}>상세페이지</Typography>
        <Tag sx={{ bgcolor: 'grey.100', color: 'text.secondary' }}>{shownMedia.length}컷</Tag>
        {hints.length ? (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>{hints.join(' · ')}</Typography>
        ) : null}
        {version?.changedAt ? (
          <Tooltip title={versionChangeHint(version)}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Tag
                icon={<Iconify icon="solar:refresh-linear" width={12} />}
                sx={{ bgcolor: 'warning.lighter', color: 'warning.dark' }}
              >
                {ymdToShort(version.changedAt)} {versionChangeLabel(version)}
              </Tag>
            </Box>
          </Tooltip>
        ) : null}
      </Stack>

      {timeline?.length && version?.ref?.postId ? (
        <DetailVersionTimeline
          timeline={timeline}
          currentSeq={currentSeq}
          activeSeq={pickedSeq}
          onSelect={setPickedSeq}
        />
      ) : null}

      {/* ⚠️ 옛 판을 보는 동안에는 «지금이 아니다»가 항상 눈에 있어야 한다 */}
      {viewingPast ? (
        <PastVersionNotice seq={pickedSeq} state={versionState} onBack={backToCurrent} />
      ) : null}

      {viewingPast && versionState.loading ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'text.secondary' }}>
          <CircularProgress size={20} />
          <Typography variant="caption">그때 상세페이지를 불러오는 중…</Typography>
        </Stack>
      ) : null}

      {shownMedia.length ? (
        <Box
          sx={{
            position: 'relative',
            maxWidth: dense ? '100%' : PAGE_WIDTH,
            mx: 'auto',
            maxHeight: expanded ? 'none' : (dense ? FOLD_HEIGHT_DENSE : FOLD_HEIGHT),
            overflow: 'hidden',
            borderRadius: RADIUS.md,
            border: '1px solid',
            borderColor: viewingPast ? 'warning.light' : 'divider',
            bgcolor: 'background.paper',
            // ⚠️ 인라인 요소 사이 여백 제거 → 컷이 끊김 없이 붙는다
            typography: 'uiMeta',
          }}
        >
          {rendered.map((item) =>
            item.type === 'video' ? (
              <AutoPlayVideo key={item.url} src={item.url} />
            ) : (
              <Box
                key={item.url}
                component="img"
                src={item.url}
                alt=""
                loading="lazy"
                sx={{ width: '100%', display: 'block', verticalAlign: 'top' }}
              />
            ),
          )}

          {!expanded ? (
            <Box
              onClick={() => setExpanded(true)}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 140,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                pb: 1.5,
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,1) 100%)',
              }}
            >
              <Typography sx={{ typography: 'uiAction', color: 'text.secondary' }}>
                펼쳐서 전체 보기
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {shownMedia.length ? (
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setExpanded((prev) => !prev)}
            endIcon={
              <Iconify
                icon={expanded ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'}
                width={16}
              />
            }
          >
            {expanded ? '상세페이지 접기' : `상세페이지 전체 보기 (${shownMedia.length}컷)`}
          </Button>
          {shownTextAll ? (
            <Button
              size="small"
              onClick={() => setShowText((prev) => !prev)}
              startIcon={<Iconify icon="solar:document-text-linear" width={16} />}
            >
              {showText ? '본문 텍스트 접기' : '본문 텍스트'}
            </Button>
          ) : null}
        </Stack>
      ) : null}

      <Collapse in={showText}>
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            maxHeight: 240,
            overflowY: 'auto',
            borderRadius: RADIUS.md,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ typography: 'uiMeta', lineHeight: 1.6, whiteSpace: 'pre-line', color: 'text.secondary' }}>
            {shownTextAll}
          </Typography>
        </Box>
      </Collapse>

      <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.disabled' }}>
        판매처 상세페이지의 이미지·영상입니다.
      </Typography>
    </>
  );
}

/**
 * 옛 판을 보는 동안 머리에 붙는 띠.
 *
 * ⚠️ 「없다」와 「못 불러왔다」를 가른다. 지문 도입 이전 수집분은 **버전 문서 자체가 없어서**
 * 그때 모습이 남아 있지 않다 — 그건 실패가 아니라 사실이라 다른 말로 적는다.
 *
 * ⚠️ **무엇이 달라졌는지는 적지 않는다.** 수집기의 분류와 우리가 보관한 필드가 어긋나서
 * (`changeType: 'text-only'` 10건 전부 본문이 동일했다) 근거가 흔들린다 — 판을 오가며
 * 눈으로 보는 것이 지금으로선 가장 정확하다.
 */
function PastVersionNotice({
  seq,
  state,
  onBack,
}: {
  seq: number;
  state: ReturnType<typeof useProductDetailVersion>;
  onBack: () => void;
}) {
  const back = (
    <Button size="small" color="inherit" onClick={onBack}>
      현재로 돌아가기
    </Button>
  );

  if (state.error) {
    return <Alert severity="error" action={back} sx={{ mb: 1.5 }}>{state.error}</Alert>;
  }
  if (state.missing) {
    return (
      <Alert severity="info" action={back} sx={{ mb: 1.5 }}>
        v{seq} 시점의 이미지는 보관돼 있지 않습니다. 이 상품은 컷 단위 보관이 시작되기 전에 수집됐고,
        바뀐 «시점»만 알 수 있습니다.
      </Alert>
    );
  }
  if (!state.data) return null;

  const period =
    state.data.lastDate && state.data.lastDate !== state.data.firstDate
      ? `${ymdToLong(state.data.firstDate)} ~ ${ymdToShort(state.data.lastDate)}`
      : ymdToLong(state.data.firstDate);

  return (
    <Alert severity="warning" icon={<Iconify icon="solar:history-linear" width={20} />} action={back} sx={{ mb: 1.5 }}>
      <Typography sx={{ typography: 'uiBody', fontWeight: 600 }}>
        {period} 일자의 버전입니다.
      </Typography>
    </Alert>
  );
}
